import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import { FaShieldAlt, FaChevronRight, FaTrash, FaPlus } from 'react-icons/fa';
import logorv from '../../resources/imgs/logo-lado-bom-seguros.svg';
import { getPreAnalyses, deletePreAnalysis, PreAnalysisResponse } from '../../services/bond-insurance-pre-analysis';

// --- Styled Components ---

const Container = styled.div`
  min-height: 100vh;
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
`;

const AppBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #FFFFFF;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  z-index: 100;
  border-bottom: 1px solid #f0f0f0;
`;

const AppLogo = styled.img`
  height: 32px;
`;

const Content = styled.div`
  flex: 1;
  padding: 24px;
  padding-top: 72px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const PreAnalysisList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreAnalysisCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'approved': return '#D4EDDA';
      case 'rejected': return '#F8D7DA';
      case 'converted': return '#D1ECF1';
      default: return '#FFF3CD';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved': return '#155724';
      case 'rejected': return '#721C24';
      case 'converted': return '#0C5460';
      default: return '#856404';
    }
  }};
`;

const CardInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const InfoBlock = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: #999;
  margin-bottom: 2px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const ActionButton = styled.button<{ variant?: string }>`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.variant === 'danger' ? `
    background: #FF3B30;
    color: white;
  ` : `
    background: #59249B;
    color: white;
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #ccc;
`;

const NewAnalysisButton = styled.button`
  background: #59249B;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #666;
`;

// --- Component ---

type Props = RouteComponentProps;

const PwaPreAnalysisList: React.FC<Props> = ({ history }) => {
  const [preAnalyses, setPreAnalyses] = useState<PreAnalysisResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadPreAnalyses();
  }, []);

  const loadPreAnalyses = async () => {
    try {
      setLoading(true);
      const data = await getPreAnalyses();
      setPreAnalyses(data);
    } catch (error) {
      console.error('Error loading pre-analyses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pré-análise?')) return;

    try {
      setDeletingId(id);
      await deletePreAnalysis(id);
      setPreAnalyses(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting pre-analysis:', error);
      alert('Erro ao excluir pré-análise');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Aprovado';
      case 'rejected': return 'Recusado';
      case 'converted': return 'Convertido';
      default: return 'Pendente';
    }
  };

  const formatCurrency = (value: number) => {
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCpf = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <Container>
      <AppBar>
        <AppLogo src={logorv} alt="Lado Bom Seguros" />
      </AppBar>

      <Content>
        <PageHeader>
          <PageTitle>Minhas Pré-Análises</PageTitle>
          <PageSubtitle>Veja o histórico de pré-análises de seguro fiança</PageSubtitle>
        </PageHeader>

        {loading ? (
          <LoadingContainer>Carregando...</LoadingContainer>
        ) : preAnalyses.length === 0 ? (
          <EmptyState>
            <EmptyIcon><FaShieldAlt /></EmptyIcon>
            <p>Nenhuma pré-análise encontrada</p>
            <p style={{ fontSize: 14 }}>Realize sua primeira pré-análise para verificar se você está apto a contratar um seguro fiança.</p>
          </EmptyState>
        ) : (
          <PreAnalysisList>
            {preAnalyses.map((preAnalysis) => (
              <PreAnalysisCard key={preAnalysis.id}>
                <CardTopRow>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>{preAnalysis.name || 'Sem nome'}</span>
                    <span style={{ fontSize: 12, color: '#666' }}>CPF: {formatCpf(preAnalysis.cpf)}</span>
                  </div>
                  <StatusBadge status={preAnalysis.status}>
                    {getStatusText(preAnalysis.status)}
                  </StatusBadge>
                </CardTopRow>

                <CardInfoRow>
                  <InfoBlock>
                    <InfoLabel>Valor do aluguel</InfoLabel>
                    <InfoValue>R$ {formatCurrency(preAnalysis.rentValue)}</InfoValue>
                  </InfoBlock>
                  <InfoBlock>
                    <InfoLabel>Limite aprovado</InfoLabel>
                    <InfoValue>R$ {formatCurrency(preAnalysis.approvedLimit)}</InfoValue>
                  </InfoBlock>
                </CardInfoRow>

                <CardInfoRow>
                  <InfoBlock>
                    <InfoLabel>Data da análise</InfoLabel>
                    <InfoValue>{formatDate(preAnalysis.createdAt)}</InfoValue>
                  </InfoBlock>
                  {preAnalysis.estate && (
                    <InfoBlock>
                      <InfoLabel>Imobiliária</InfoLabel>
                      <InfoValue>{preAnalysis.estate.name}</InfoValue>
                    </InfoBlock>
                  )}
                </CardInfoRow>

                <CardActions>
                  {preAnalysis.status !== 'converted' && (
                    <ActionButton
                      variant="danger"
                      onClick={() => handleDelete(preAnalysis.id)}
                      disabled={deletingId === preAnalysis.id}
                    >
                      <FaTrash size={12} />
                      {deletingId === preAnalysis.id ? 'Excluindo...' : 'Excluir'}
                    </ActionButton>
                  )}
                  {preAnalysis.status === 'approved' && (
                    <ActionButton onClick={() => history.push(`/pwa/bond-insurance-analysis?preAnalysisId=${preAnalysis.id}`)}>
                      Continuar <FaChevronRight size={12} />
                    </ActionButton>
                  )}
                </CardActions>
              </PreAnalysisCard>
            ))}
          </PreAnalysisList>
        )}

        <NewAnalysisButton onClick={() => history.push('/pwa/bond-insurance-analysis')}>
          <FaPlus size={16} /> Nova Pré-Análise
        </NewAnalysisButton>
      </Content>
    </Container>
  );
};

export default PwaPreAnalysisList;
